import type { ReactNode } from 'react';

import {
  Alert,
  Icons,
  LayoutCard,
  Skeleton,
  Stack,
  StatusChipV2,
  StatusChipV2Props,
  Typography,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { OPTIMIZATION_GOAL_TO_KENT_CONSOLIDATION_CONFIG } from '@cast/one-click-features-evictor';
import { isAtLeastOneAggressiveModeConfigEnabled } from '@cast/split-me/features/cluster/rebalance';
import { formatPercentage, notReachable } from '@cast/utils';

import { MetricsGrid } from './MetricsGrid';
import { OPTIMIZATION_GOAL_TO_AUTOSCALER_EVICTOR_CONFIG } from '../../../_constants';
import { useAutoOptimization } from '../../../_hooks/useAutoOptimization';

import type { OptimizationGoal } from '../../../open-api-types/autopilot-types';
import type { MetricsGridItem } from './_types';
import { KentBinPackingFlowState } from '../../../_types';

const formatSeconds = (seconds: number) => `${Math.round(seconds / 60)} min`;

type SectionStatus = 'error' | 'loading' | 'ready';

const STATUS_TO_CHIP_CONFIG: Record<
  SectionStatus,
  Pick<StatusChipV2Props, 'label' | 'color' | 'loading'>
> = {
  error: { label: 'Error', color: 'error' },
  loading: { label: 'Loading', color: 'neutral', loading: true },
  ready: { label: 'Ready', color: 'success' },
};

const Layout = ({
  status,
  children,
}: {
  status: SectionStatus;
  children: ReactNode;
}) => (
  <LayoutCard
    headerEndContent={<StatusChipV2 {...STATUS_TO_CHIP_CONFIG[status]} />}
    title={
      <Stack direction="row" alignItems="center" gap={8}>
        <Icons.PaintBrushHousehold size={18} />
        <Typography variant="h6">Bin-packing</Typography>
      </Stack>
    }
    headerDivider
    padding={false}
    slotProps={{
      content: {
        sx: { p: 16 },
      },
    }}
  >
    {children}
  </LayoutCard>
);

const KentConsolidationSettings = ({
  optimizationGoal,
}: {
  optimizationGoal: OptimizationGoal;
}) => {
  const preset =
    OPTIMIZATION_GOAL_TO_KENT_CONSOLIDATION_CONFIG[optimizationGoal];

  const rows: MetricsGridItem[][] = [
    [
      {
        label: 'TTL setting',
        value: formatSeconds(preset.minNodeAgeSeconds),
      },
      {
        label: 'Aggressive mode',
        value: isAtLeastOneAggressiveModeConfigEnabled(
          preset.aggressiveModeConfig
        )
          ? 'Enabled'
          : 'Disabled',
      },
      {
        label: 'Rebalancing interval',
        value: formatSeconds(preset.fullModeCycleIntervalSeconds),
      },
      {
        label: 'Savings threshold',
        value: formatPercentage(preset.savingsThresholdPercent),
      },
    ],
  ];

  return (
    <Layout status="ready">
      <MetricsGrid rows={rows} columnsPerRow={4} />
    </Layout>
  );
};

type KentConsolidationSectionProps = {
  binPackingFlowState: KentBinPackingFlowState;
  optimizationGoal: OptimizationGoal;
};
const KentConsolidationSection = ({
  binPackingFlowState,
  optimizationGoal,
}: KentConsolidationSectionProps) => {
  switch (binPackingFlowState.type) {
    case 'checking-crb-compatibility':
      return (
        <Layout status="loading">
          <Skeleton width="100%" height={50} />
        </Layout>
      );
    case 'checking-crb-compatibility-error':
      return (
        <Layout status="error">
          <FailedToLoad
            title="Failed to load bin-packing settings"
            refresh={() => binPackingFlowState.retry()}
            compact
          />
        </Layout>
      );
    case 'checking-crb-compatibility-complete': {
      if (binPackingFlowState.isCompatible) {
        return (
          <KentConsolidationSettings optimizationGoal={optimizationGoal} />
        );
      }
      return (
        <Layout status="error">
          <Alert
            color="warning"
            title="Bin-packing is not compatible with the current kentroller version."
          >
            Please update kentroller to the latest version to use bin-packing.
          </Alert>
        </Layout>
      );
    }
    default:
      return notReachable(binPackingFlowState);
  }
};

type AutoscalerEvictorConfigSectionProps = {
  optimizationGoal: OptimizationGoal;
};

const AutoscalerEvictorConfigSection = ({
  optimizationGoal,
}: AutoscalerEvictorConfigSectionProps) => {
  const preset =
    OPTIMIZATION_GOAL_TO_AUTOSCALER_EVICTOR_CONFIG[optimizationGoal];

  return (
    <Layout status="ready">
      <MetricsGrid
        rows={[
          [
            {
              label: 'Node Grace Period',
              value: `${preset.nodeGracePeriodMinutes} min`,
            },
            {
              label: 'Max nodes per cycle',
              value: preset.maxTargetNodesPerCycle,
            },
            {
              label: 'Cycle interval',
              value: preset.cycleInterval,
            },
            {
              label: 'PDB Behavior',
              value: preset.ignorePodDisruptionBudgets
                ? 'Ignored'
                : 'Always respected',
            },
          ],
        ]}
        columnsPerRow={4}
      />
    </Layout>
  );
};

export const BinPackingSection = () => {
  const { binPackingFlowState, state } = useAutoOptimization();

  const optimizationGoal = state.preferences.priority;

  if (!optimizationGoal) {
    // shouldn't happen, because recommendation screen is after priority screen
    // but we need to narrow the type and safegaurd against this case
    return null;
  }

  switch (binPackingFlowState.mode) {
    case 'kent':
      return (
        <KentConsolidationSection
          binPackingFlowState={binPackingFlowState.kentBinPackingFlowState}
          optimizationGoal={optimizationGoal}
        />
      );
    case 'autoscaler':
      return (
        <AutoscalerEvictorConfigSection optimizationGoal={optimizationGoal} />
      );
    default:
      return notReachable(binPackingFlowState);
  }
};
